import type { PresignedSkillFileUpload } from "@cohesive/api-client";

/**
 * Upload a skill file to S3 using a presigned response from the skills API.
 *
 * Backwards compatible with both presign shapes the backend may return:
 * - method "POST": an S3 presigned POST — submit the policy `field_values` plus
 *   the file as multipart form data. The browser sets the multipart boundary, so
 *   we must NOT set a Content-Type header or S3 rejects with 403. Policy fields go
 *   first and the `file` field goes last; the object content type is carried by the
 *   `Content-Type` form field.
 * - any other method (e.g. legacy "PUT"): upload the body straight to the URL with
 *   an explicit Content-Type header.
 *
 * The content type is taken from `options.contentType`, falling back to the blob's
 * own type, then `application/octet-stream`. Pass it explicitly when the blob is
 * untyped (e.g. JSZip entries) so the object doesn't land as octet-stream.
 *
 * Throws on a non-2xx response.
 */
export async function uploadPresignedSkillFile(
  presigned: PresignedSkillFileUpload,
  body: Blob,
  options?: { contentType?: string; signal?: AbortSignal },
): Promise<void> {
  const signal = options?.signal;
  const contentType =
    options?.contentType || body.type || "application/octet-stream";

  if (presigned.method === "POST") {
    const form = new FormData();
    for (const [key, value] of Object.entries(presigned.field_values ?? {})) {
      form.append(key, value);
    }
    form.append("Content-Type", contentType);
    form.append("file", body);

    const res = await fetch(presigned.url, {
      method: "POST",
      body: form,
      signal,
    });
    if (!res.ok) {
      throw new Error(`Upload failed (${res.status} ${res.statusText})`);
    }
    return;
  }

  const res = await fetch(presigned.url, {
    method: presigned.method,
    headers: { "Content-Type": contentType },
    body,
    signal,
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status} ${res.statusText})`);
  }
}
